import { useState } from "react";
import { useAppSelector } from "../../../../app/hooks";
import { useScrollToSection } from "../../../../app/useScrollToSection";
import RtkqResultViewer from "../../../../components/RtkqResultViewer";
import { SelectDevelopmentView } from "../../../../components/input/SelectDevelopmentView";
import { Loading } from "../../../../components/Loading";
import { DownloadButton } from "../../shared/DownloadButton";
import { CrPromptAnalysisTable } from "./CrPromptAnalysisTable";
import { EssayPromptAnalysisTable } from "./EssayPromptAnalysisTable";
import { EssayPromptAnalysisChart } from "./EssayPromptAnalysisChart";
import { CrPromptAnalysisChart } from "./CrPromptAnalysisChart";
import { useCoursePromptReportQuery } from "./coursePromptReportApiSlice";

export const CLASS_PROMPT_ANALYSIS = "prompt-analysis";
export const CLASS_CR_PROMPT_ANALYSIS = "cr-prompt-analysis";

export default function CoursePromptReport() {
	const { aspUrl, config } = useAppSelector((state) => state.site);
	useScrollToSection();

	const [view, setView] = useState("react");

	const result = useCoursePromptReportQuery({});
	const data = result.data;
	return (
		<section>
			<div className="flex justify-between">
				<div className="w-3/4">
					<h1>Course Prompt Report</h1>
				</div>
				<SelectDevelopmentView
					value={view}
					setValue={(v) => setView(v as string)}
					simpleListBoxId="viewFilter"
					options={[
						{ value: "react", label: "React" },
						{ value: "raw", label: "Raw Data" },
					]}
					fieldClasses="w-64"
				/>
			</div>
			{view == "react" && (
				<>
					<section id={CLASS_PROMPT_ANALYSIS} className="mt-8">
						<h2>Prompt Analysis</h2>
						<p className="mt-4">
							The report below provides a list of prompts for a specific class, the
							number of essays and drafts, and the average number of drafts per essay.
							The report also shows the individual{" "}
							{config?.isUtahComposeSite ? "writing element" : "trait"} and overall
							averages for the most recent drafts. Click on the prompt name to see the
							students who responded to the prompt, the date of their most recent
							drafts, and their most recent scores.
						</p>
						<Loading loading={result.isLoading} />
						{data && (
							<>
								<div className="flex place-content-end w-full mt-4">
									<DownloadButton
										url={`${aspUrl}/Report/DownloadPromptUsageFilteredByCourse?courseId=${data.courseId}`}
										disabled={data.promptUsageByPrompt.length == 0}
									/>
								</div>
								<EssayPromptAnalysisTable
									className="mt-4"
									courseId={data.courseId}
									promptData={data.promptUsageByPrompt}
									classData={data.promptUsageForCourse}
								/>
								<EssayPromptAnalysisChart
									className="mt-8 p-4 border-2 border-gray-200"
									courseId={data.courseId}
									promptData={data.promptUsageByPrompt}
									classData={data.promptUsageForCourse}
								/>
							</>
						)}
					</section>

					<section id={CLASS_CR_PROMPT_ANALYSIS} className="mt-8">
						<h2>Constructed Response Prompt Analysis</h2>
						<p className="mt-4">
							The report below provides a list of prompts for a specific class, the
							number of responses and drafts, and the average number of drafts per
							response. The report shows the scores for the most recent drafts. Click
							on the prompt name to see the students who responded to the prompt, the
							date of their most recent drafts, and their most recent scores.
						</p>
						<Loading loading={result.isLoading} />
						{data && (
							<>
								<div className="flex place-content-end w-full mt-4">
									<DownloadButton
										url={`${aspUrl}/Report/DownloadConstructedResponsePromptUsageFilteredByCourse?courseId=${data.courseId}`}
										disabled={
											data.constructedResponsePromptUsageByPrompt.length == 0
										}
									/>
								</div>
								<CrPromptAnalysisTable
									className="mt-4"
									courseId={data.courseId}
									promptData={data.constructedResponsePromptUsageByPrompt}
									classData={data.constructedResponsePromptUsageForCourse}
								/>
								<CrPromptAnalysisChart
									className="mt-8 p-4 border-2 border-gray-200"
									courseId={data.courseId}
									promptData={data.constructedResponsePromptUsageByPrompt}
									classData={data.constructedResponsePromptUsageForCourse}
								/>
							</>
						)}
					</section>
				</>
			)}
			{view == "raw" && <RtkqResultViewer heading="Prompt Report Data" result={result} />}
		</section>
	);
}

export function promptScoresReportLink(promptId: number, courseId: number) {
	return `/reports/course/prompt/scores?promptId=${promptId}&courseId=${courseId}`;
}
